import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Save, Send, X, FileText, User, Calendar, Briefcase, MessageSquare, Loader2, Building2, CreditCard
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import InvoiceItemsTable from '../components/ui/InvoiceItemsTable';
import { useToast } from '../hooks/useToast';
import Checkbox from '../components/ui/Checkbox';
import {
    fetchUtilCustomers,
    fetchPOSCustomers,
    fetchUtilStaff,
    fetchCatentriesUtil,
    fetchStores, fetchStoresWhereStaffAssigned,
    fetchStaffViewCreditline,
    readTaxes,
    fetchDiscountRules,
    fetchGroupsUtils,
    fetchGroupsForInvoice
} from '../apiHelper';
import { client } from '../apiHelper';

const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 6);
    return date.toISOString().split('T')[0];
}

const NewInvoice = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const [formData, setFormData] = useState({
        customerId: '',
        storeId: '',
        invoiceNumber: '',
        invoiceDate: new Date().toISOString().slice(0, 16),
        dueDate: '',
        salesPersonId: '',
        subject: '',
    });

    const { groupId = null, selectedMemberIds = [] } = location.state || {};

    const [lineItems, setLineItems] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdown options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [salesPersonOptions, setSalesPersonOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');

    // Credit line state
    const [creditline, setCreditline] = useState(null);
    const [loadingCreditline, setLoadingCreditline] = useState(false);

    // Loading states
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [loadingStores, setLoadingStores] = useState(true);
    const [loadingItems, setLoadingItems] = useState(true);

    const [taxOptions, setTaxOptions] = useState([]);
    const [discountOptions, setDiscountOptions] = useState([]);
    const [loadingTaxes, setLoadingTaxes] = useState(true);
    const [loadingDiscounts, setLoadingDiscounts] = useState(true);

    const [invoiceMode, setInvoiceMode] = useState(groupId ? 'group' : 'single');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupOptions, setGroupOptions] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(true);

    const [sendEmail, setSendEmail] = useState(false)


    // Load user from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to create invoices.', 'info');
            navigate('/login');
        }
    }, [showToast, navigate]);

    // Fetch Member Groups
    useEffect(() => {
        if (!user || !selectedStore) return;

        const loadGroups = async () => {
            setLoadingGroups(true);
            try {
                // const groups = await fetchGroupsUtils();
                // console.log(selectedStore)
                const groups = await fetchGroupsForInvoice({
                    offset: 0,
                    limit: 100000,
                    store_id: selectedStore,
                });
                const mappedOptions = groups.map(g => ({
                    label: `${g.name} (${g.members?.length || 0} members)`,
                    value: g.mbrgrp_id,
                    groupData: g
                }));
                // console.log(groups)
                setGroupOptions(mappedOptions);

                if (groupId && selectedMemberIds.length > 0) {
                    const targetOption = mappedOptions.find(opt => opt.value === Number(groupId));
                    if (targetOption) {
                        const filteredMembers = targetOption.groupData.members.filter(member =>
                            selectedMemberIds.includes(member.member_id)
                        );
                        const updatedGroupSelection = {
                            ...targetOption,
                            label: `${targetOption.groupData.name} (${filteredMembers.length} of ${targetOption.groupData.members.length} members selected)`,
                            groupData: {
                                ...targetOption.groupData,
                                members: filteredMembers
                            }
                        };
                        setSelectedGroup(updatedGroupSelection);
                    }
                }
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load customer groups', 'error');
                setGroupOptions([]);
            } finally {
                setLoadingGroups(false);
            }
        };

        loadGroups();
    }, [user, selectedStore, showToast, groupId, selectedMemberIds.join(',')]);

    // Fetch Customers
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        // FIX: Add !selectedStore guard clause to prevent running with an empty string
        if (!user || !selectedStore) return;

        setLoadingCustomers(true);
        try {
            const customers = await fetchPOSCustomers({ ...user, store_id: selectedStore }, searchQuery);
            setCustomerOptions(customers.map(c => {
                const u = c.user || {};
                const firstName = (u.first_name || '').trim();
                const middleName = (u.middle_name || '').trim();
                const lastName = (u.last_name || '').trim();
                const fullName = firstName || lastName ? `${firstName} ${middleName || ''} ${lastName}`.trim() : 'Unnamed Customer';
                const logonId = u.credential?.logon_id || 'No ID';
                return {
                    label: `${fullName} (${logonId})`,
                    value: c.member_id,
                    customerData: c
                };
            }));
        } catch (err) {
            showToast('Failed to load customers', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, selectedStore, showToast]);

    useEffect(() => {
        // FIX: Only invoke if both user AND a selectedStore are ready
        if (user && selectedStore) {
            fetchCustomers();
        }
    }, [user, selectedStore, fetchCustomers]);

    // Fetch Sales Staff
    useEffect(() => {
        if (!user) return;
        const loadStaff = async () => {
            setLoadingStaff(true);
            try {
                const staff = await fetchUtilStaff(user);
                setSalesPersonOptions(staff.map(s => ({
                    label: `${s.user.first_name} ${s.user.last_name}`,
                    value: s.member_id,
                    staffData: s
                })));
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load staff', 'error');
            } finally {
                setLoadingStaff(false);
            }
        };
        loadStaff();
    }, [user, showToast]);

    // Fetch Stores (Branches)
    useEffect(() => {
        if (!user) return;

        const loadStores = async () => {
            setLoadingStores(true);
            try {
                const stores = await fetchStoresWhereStaffAssigned(user);

                // Safety Check: handle cases where stores array might be empty
                if (stores && stores.length > 0) {
                    const mappedStores = stores.map(store => ({
                        label: store.identifier || `Branch ${store.store_id}`,
                        value: store.store_id,
                        storeData: store
                    }));

                    setSelectedStore(mappedStores[0].value);
                    setFormData(prev => ({ ...prev, 'storeId': mappedStores[0].value }));
                    setStoreOptions(mappedStores);
                } else {
                    setStoreOptions([]);
                }
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load branches', 'error');
                setStoreOptions([]);
            } finally {
                setLoadingStores(false);
            }
        };

        loadStores();
    }, [user, showToast]);

    // Fetch Catalog Items
    useEffect(() => {
        if (!user) return;
        const loadItems = async () => {
            setLoadingItems(true);
            try {
                const items = await fetchCatentriesUtil(user);
                setItemOptions(items.map(item => ({
                    value: item.catentry_id,
                    label: item.productname,
                    price: parseFloat(item.listprice),
                    currency: item.currency,
                    type: item.catalog_type,
                    total_quantity: item.total_quantity
                })));
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load items from catalog', 'error');
                setItemOptions([]);
            } finally {
                setLoadingItems(false);
            }
        };
        loadItems();
    }, [user, showToast]);

    // ← NEW: Fetch Credit Line
    useEffect(() => {
        const checkCreditline = async () => {
            if (!formData.customerId || !formData.storeId || !user?.users_id) {
                setCreditline(null);
                return;
            }

            setLoadingCreditline(true);
            try {
                const payload = {
                    store_id: Number(formData.storeId),
                    customer_id: Number(formData.customerId),
                    referer_id: user.users_id
                };
                const result = await fetchStaffViewCreditline(payload);
                setCreditline(result || null);
            } catch (err) {
                setCreditline(null);
            } finally {
                setLoadingCreditline(false);
            }
        };

        checkCreditline();
    }, [formData.customerId, formData.storeId, user?.users_id]);

    useEffect(() => {
        if (user?.users_id && salesPersonOptions.length > 0) {
            const currentUserOption = salesPersonOptions.find(
                opt => opt.value === user.users_id   // match by member_id
            );

            if (currentUserOption) {
                setFormData(prev => ({
                    ...prev,
                    salesPersonId: currentUserOption.value
                }));
            }
        }
    }, [user?.users_id, salesPersonOptions]);

    // Fetch Taxes
    useEffect(() => {
        if (!user) return;

        const loadTaxes = async () => {
            setLoadingTaxes(true);
            try {
                const taxes = await readTaxes(user);
                setTaxOptions(taxes.map(tax => ({
                    label: `${tax.code} – ${tax.identifier} (${(tax.rate).toFixed(1)}%)`,
                    value: tax.taxcategory_id.toString(),
                    rate: parseFloat(tax.rate),
                    code: tax.code
                })));
            } catch (err) {
                showToast('Failed to load taxes', 'error');
                setTaxOptions([]);
            } finally {
                setLoadingTaxes(false);
            }
        };

        loadTaxes();
    }, [user, showToast]);

    // Fetch Discount Rules
    useEffect(() => {
        if (!user) return;

        const loadDiscounts = async () => {
            setLoadingDiscounts(true);
            try {
                const rules = await fetchDiscountRules({ ...user, offset: 0, limit: 1000 });
                // console.log(rules)
                setDiscountOptions(rules.map(rule => ({
                    label: `${rule.name} – ${rule.description}`,
                    value: rule.discountrule_id.toString(),
                    type: rule.discount_type, // 'percentage' | 'fixed'
                    valueAmount: parseFloat(rule.value),
                    appliesTo: rule.applies_to, // 'product' | 'order'
                    targetProductId: rule.target_product_id,
                    targetCategoryId: rule.target_category_id,
                    minimumPurchaseAmount: rule.minimum_purchase_amount ? parseFloat(rule.minimum_purchase_amount) : null,
                    minimumPurchaseQuantity: rule.minimum_purchase_quantity,
                    startDate: rule.start_date,
                    endDate: rule.end_date,
                    isActive: rule.is_active,
                    requiresCouponCode: rule.requires_coupon_code,
                    couponCode: rule.coupon_code,
                    customers: rule.customers?.map(c => c.member_id) || [],
                    stores: rule.stores?.map(s => s.store_id) || [],
                    products: rule.products?.map(p => p.catentry_id) || [],
                })));
            } catch (err) {
                showToast('Failed to load discount rules', 'error');
                setDiscountOptions([]);
            } finally {
                setLoadingDiscounts(false);
            }
        };

        loadDiscounts();
    }, [user, showToast]);

    useEffect(() => {
        if (!formData.invoiceDate) return;

        const invoiceDate = new Date(formData.invoiceDate);
        const dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 6); // 6 days from invoice date

        const formattedDueDate = dueDate.toISOString().split('T')[0]; // YYYY-MM-DD

        // Only update if dueDate is empty OR if it was auto-generated (prevents overwriting manual edits)
        if (!formData.dueDate || formData.dueDate === getPreviousAutoDueDate()) {
            setFormData(prev => ({
                ...prev,
                dueDate: formattedDueDate
            }));
        }

        // Helper to remember what the last auto-generated date was
        function getPreviousAutoDueDate() {
            if (!formData.invoiceDate) return null;
            const prev = new Date(formData.invoiceDate);
            prev.setDate(prev.getDate() + 6);
            return prev.toISOString().split('T')[0];
        }
    }, [formData.invoiceDate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "storeId") setSelectedStore(value);
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleItemsChange = (items) => {
        setLineItems(items);
    };

    const validateForm = () => {
        // Mode-specific customer validation
        if (invoiceMode === 'single') {
            if (!formData.customerId) return 'Customer is required.';
        } else {
            if (!selectedGroup) return 'Please select a customer group.';
            if (!selectedGroup.groupData?.members?.length) return 'Selected group has no members.';
        }

        if (!formData.storeId) return 'Branch is required.';
        if (!formData.invoiceDate) return 'Invoice Date is required.';
        if (!formData.dueDate) return 'Due Date is required.';
        if (lineItems.length === 0) return 'Please add at least one item.';
        if (new Date(formData.dueDate) < new Date(formData.invoiceDate))
            return 'Due Date cannot be before Invoice Date.';

        // CREDIT LINE CHECK — ONLY FOR SINGLE CUSTOMER MODE
        if (invoiceMode === 'single' && formData.customerId && formData.storeId) {
            if (creditline === null) {
                return 'This customer has no active credit line at the selected branch. Cannot create invoice.';
            }
            // Optional: stricter check (recommended)
            const limit = Number(creditline.credit_limit || 0);
            const used = Number(creditline.current_balance || 0);
            if (limit <= 0 || used >= limit) {
                return 'Customer has no available credit at this branch.';
            }
        }

        // Group mode bypasses creditline check entirely → allowed
        return null;
    };

    const handleSave = async (status, shouldSendEmail = false) => {
        const error = validateForm();
        if (error) {
            showToast(error, 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            // Re-calculate every line with full accounting
            const enrichedItems = lineItems.map(item => {
                const selectedItem = itemOptions.find(opt => opt.value === item.itemId);
                const selectedDiscount = discountOptions.find(d => d.value === item.discountId);
                const selectedTax = taxOptions.find(t => t.value === item.taxId);

                const price = parseFloat(item.rate || selectedItem?.price || 0);
                const qty = parseFloat(item.quantity || 1);
                const subtotal = price * qty;

                // let discountAmount = 0;
                // if (selectedDiscount) {
                //     if (selectedDiscount.type === 'percentage') {
                //         discountAmount = subtotal * (selectedDiscount.valueAmount / 100);
                //     } else if (selectedDiscount.type === 'fixed') {
                //         discountAmount = selectedDiscount.valueAmount;
                //     }
                // }

                let discountAmount = 0;

                if (selectedDiscount) {
                    const now = new Date();
                    const validDate = (!selectedDiscount.startDate || now >= new Date(selectedDiscount.startDate)) &&
                        (!selectedDiscount.endDate || now <= new Date(selectedDiscount.endDate));

                    const validCustomer = !selectedDiscount.customers?.length ||
                        selectedDiscount.customers.includes(Number(formData.customerId));

                    if (validDate && validCustomer) {
                        const qty = parseFloat(item.quantity || 1);
                        const subtotal = price * qty;
                        const discountValue = parseFloat(selectedDiscount.valueAmount || selectedDiscount.value || 0);

                        if (selectedDiscount.type === 'percentage' || selectedDiscount.discount_type === 'percentage') {
                            discountAmount = subtotal * (discountValue / 100);
                        }
                        else if (selectedDiscount.type === 'fixed' || selectedDiscount.discount_type === 'fixed') {
                            discountAmount = discountValue;
                        }
                        else if ((selectedDiscount.type === 'tiered_pricing' || selectedDiscount.discount_type === 'tiered_pricing')
                            && selectedDiscount.tiers?.length > 0) {

                            const applicableTier = selectedDiscount.tiers.find(tier =>
                                qty >= (tier.min_quantity || 0) &&
                                (!tier.max_quantity || qty <= tier.max_quantity)
                            );

                            if (applicableTier) {
                                discountAmount = parseFloat(applicableTier.tier_value || 0) * qty;
                            }
                        }
                    }
                }

                const taxableAmount = subtotal - discountAmount;
                const taxRate = (selectedTax?.rate || 0) / 100;
                const taxAmount = taxableAmount * taxRate;
                const total = taxableAmount + taxAmount;

                return {
                    ...item,
                    unit_price: price,
                    quantity: qty,
                    subtotal: Number(subtotal.toFixed(2)),
                    discount_amount: Number(discountAmount.toFixed(2)),
                    tax_amount: Number(taxAmount.toFixed(2)),
                    total: Number(total.toFixed(2)),
                    product_id: item.itemId ? Number(item.itemId) : null,
                    discount_rule_id: item.discountId ? Number(item.discountId) : null,
                    tax_category_id: item.taxId ? Number(item.taxId) : null,
                };
            });

            // Calculate totals once
            const invoice_subtotal = enrichedItems.reduce((sum, i) => sum + i.subtotal, 0);
            const invoice_discount_total = enrichedItems.reduce((sum, i) => sum + i.discount_amount, 0);
            const invoice_tax_total = enrichedItems.reduce((sum, i) => sum + i.tax_amount, 0);
            const invoice_total = invoice_subtotal - invoice_discount_total + invoice_tax_total;

            // Determine customer IDs
            const customerIds = invoiceMode === 'single'
                ? [Number(formData.customerId)]
                : selectedGroup.groupData.members.map(m => Number(m.member_id));

            // Base payload (shared)
            const basePayload = {
                ...formData,
                store_id: Number(formData.storeId),
                sales_person_id: formData.salesPersonId ? Number(formData.salesPersonId) : null,
                attendant_id: user.users_id,
                status,
                invoiceNumber: formData.invoiceNumber || `INV-${Date.now()}`,
                items: enrichedItems,
                invoice_subtotal,
                invoice_discount_total,
                invoice_tax_total,
                invoice_total,
                // NEW: Support both single and bulk
                customer_ids: customerIds,  // ← array, always
                is_group_invoice: invoiceMode === 'group',
                group_id: invoiceMode === 'group' ? selectedGroup.value : null,
                group_name: invoiceMode === 'group' ? selectedGroup.label : null,

                send_email: shouldSendEmail,
            };

            // For backward compatibility: keep customer_id for single
            if (invoiceMode === 'single') {
                basePayload.customer_id = customerIds[0];
            }

            console.log('Sending invoice payload:', basePayload);

            const response = await client.postData('/sales/create-invoice', basePayload);

            const count = customerIds.length;
            const msg = invoiceMode === 'group'
                ? `Invoice successfully sent to ${count} customer${count > 1 ? 's' : ''}!`
                : (response.msg || 'Invoice created and sent successfully!');

            showToast(msg, 'success');
            // navigate('/sales/invoices');

        } catch (err) {
            console.error('Failed to create invoice:', err);
            const errorMessage =
                err.response?.data?.detail ||
                err.response?.data?.msg ||
                err.message ||
                'Failed to create invoice. Please try again.';
            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (invoiceMode === 'group' && selectedGroup) {
            setSendEmail(true);
        }
    }, [invoiceMode, selectedGroup]);

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="animate-spin h-10 w-10 text-gmail-blue" />
            </div>
        );
    }

    return (
        <div className="w-full mx-auto p-4 md:p-8 max-w-7xl bg-zinc-50/30 min-h-screen">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                {/* Main Content Workspace */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Modern Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
                        <div>
                            <div className="flex items-center gap-2.5 mb-1">
                                <div className="p-2 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600 shadow-sm">
                                    <FileText size={22} />
                                </div>
                                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
                                    New Invoice
                                </h1>
                            </div>
                            <p className="text-zinc-500 text-sm pl-1">Create and send professional invoices to your customers.</p>
                        </div>
                    </div>

                    {/* Sales Context */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white">
                        <div className="flex items-center gap-2 mb-5 pb-3 border-b border-zinc-100">
                            <Building2 size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Sales Context</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <SearchableSelectBox
                                name="storeId"
                                label="Issuing Branch"
                                required
                                value={formData.storeId}
                                onChange={handleInputChange}
                                options={storeOptions}
                                loading={loadingStores}
                                placeholder="Select branch..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <SearchableSelectBox
                                name="salesPersonId"
                                label="Sales Person"
                                value={formData.salesPersonId}
                                onChange={handleInputChange}
                                readOnly={true}
                                isDisabled={true}
                                options={salesPersonOptions}
                                loading={loadingStaff}
                                placeholder="Select sales person..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                    </Card>

                    {/* Customer Selection & Mode Toggle */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white space-y-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <User size={18} className="text-indigo-600" />
                                <h3 className="font-semibold text-sm text-zinc-800">Target Customer</h3>
                            </div>
                            <div className="flex items-center bg-white border border-zinc-200 p-1 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setInvoiceMode('single');
                                        setFormData(prev => ({ ...prev, customerId: '' }));
                                        setSelectedGroup(null);
                                    }}
                                    className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${invoiceMode === 'single' ? 'bg-indigo-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
                                >
                                    Single
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setInvoiceMode('group');
                                        setFormData(prev => ({ ...prev, customerId: '' }));
                                        setSelectedGroup(null);
                                    }}
                                    className={`px-4 py-1.5 text-xs font-medium rounded-md transition-all ${invoiceMode === 'group' ? 'bg-indigo-600 text-white' : 'text-zinc-600 hover:bg-zinc-50'}`}
                                >
                                    Group
                                </button>
                            </div>
                        </div>

                        {invoiceMode === 'single' ? (
                            <SearchableSelectBox
                                name="customerId"
                                label="Customer"
                                required
                                value={formData.customerId}
                                onChange={handleInputChange}
                                options={customerOptions}
                                onSearch={fetchCustomers}
                                loading={loadingCustomers}
                                placeholder="Search customer..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        ) : (
                            <SearchableSelectBox
                                name="groupId"
                                label="Customer Group"
                                required
                                value={selectedGroup?.value || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const group = groupOptions.find(opt => opt.value == val);
                                    setSelectedGroup(group || null);
                                }}
                                options={groupOptions}
                                loading={loadingGroups}
                                placeholder="Select group..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        )}

                        {invoiceMode === 'group' && selectedGroup && (
                            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-lg text-sm">
                                <p className="font-medium text-indigo-900">
                                    {selectedGroup.groupData.members?.length || 0} customers will receive this invoice
                                </p>
                            </div>
                        )}
                    </Card>

                    {/* Dates & Invoice Number */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <Input
                                name="invoiceNumber"
                                label="Invoice Number"
                                placeholder="Auto-generated if blank"
                                value={formData.invoiceNumber}
                                onChange={handleInputChange}
                            />
                            <Input
                                type="datetime-local"
                                name="invoiceDate"
                                label="Invoice Date"
                                required
                                value={formData.invoiceDate}
                                onChange={handleInputChange}
                                step="60"
                            />
                            <Input
                                type="date"
                                name="dueDate"
                                label="Due Date"
                                required
                                value={formData.dueDate}
                                onChange={handleInputChange}
                            />
                        </div>
                    </Card>

                    {/* Subject / Notes */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white">
                        <div className="flex items-center gap-2 mb-4">
                            <MessageSquare size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Additional Notes</h3>
                        </div>
                        <Input
                            name="subject"
                            label="Subject (Optional)"
                            placeholder="Brief description visible to the customer"
                            value={formData.subject}
                            onChange={handleInputChange}
                        />
                    </Card>

                    {/* Line Items */}
                    <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="font-bold text-sm text-zinc-800 flex items-center gap-2">
                                <Briefcase size={16} className="text-indigo-600" /> Line Items
                            </h3>
                            <span className="text-[11px] text-zinc-400 bg-zinc-100 border border-zinc-200/60 px-2 py-0.5 rounded-full font-medium">
                                Currency: ₦
                            </span>
                        </div>

                        {loadingItems || loadingTaxes || loadingDiscounts ? (
                            <Card className="p-12 border-zinc-200/60 shadow-xs rounded-xl bg-white text-center">
                                <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600 mb-3" />
                                <p className="text-zinc-500 text-xs font-medium">Loading catalog components...</p>
                            </Card>
                        ) : (
                            <div className="border border-zinc-200/60 shadow-xs rounded-xl bg-white overflow-hidden transition-all">
                                <InvoiceItemsTable
                                    itemOptions={itemOptions}
                                    taxOptions={taxOptions}
                                    discountOptions={discountOptions}
                                    currencySymbol="₦"
                                    onItemsChange={handleItemsChange}
                                    customerId={formData.customerId}
                                    storeId={formData.storeId}
                                    allItems={lineItems}
                                />
                            </div>
                        )}
                    </div>

                    {/* Email Option */}
                    <Card className="p-4 flex items-center gap-3 border-zinc-200/60">
                        <input
                            type="checkbox"
                            id="sendEmail"
                            name="sendEmail"
                            checked={sendEmail}
                            onChange={(e) => setSendEmail(e.target.checked)}
                            className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-500 border-zinc-300"
                        />
                        <label htmlFor="sendEmail" className="text-sm font-medium select-none cursor-pointer">
                            Send invoice via email to customer(s)
                        </label>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-3.5 pt-6 border-t border-zinc-200/80">
                        <Button
                            variant="ghost"
                            className="w-full sm:w-auto flex items-center justify-center text-zinc-600 hover:text-zinc-900 font-medium"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            <X size={16} className="mr-2" /> Cancel
                        </Button>
                        <Button
                            className="w-full sm:w-auto flex items-center justify-center font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-indigo-100 transition-all duration-150 px-6"
                            variant="primary"
                            onClick={() => handleSave('SENT', sendEmail)}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" /> Processing...
                                </>
                            ) : (
                                <>
                                    <Send size={16} className="mr-2" /> Save & Send Invoice
                                </>
                            )}
                        </Button>
                    </div>

                </div>

                {/* Right Side Panel - Credit / Summary */}
                <div className="lg:col-span-1 lg:sticky lg:top-6">
                    {invoiceMode === 'single' ? (
                        <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white relative overflow-hidden">
                            <div className="absolute top-0 left-0 right-0 h-[3px] bg-indigo-600" />
                            <div className="flex items-center justify-between mb-5 pb-3 border-b border-zinc-100">
                                <div className="flex items-center gap-2">
                                    <CreditCard size={18} className="text-indigo-600" />
                                    <h3 className="font-bold text-xs text-zinc-700 uppercase tracking-wide">Credit Line</h3>
                                </div>
                            </div>

                            {loadingCreditline ? (
                                <div className="text-center py-12">
                                    <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600" />
                                </div>
                            ) : creditline ? (
                                <div className="space-y-5">
                                    <div className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                                        <p className="text-[11px] font-bold uppercase tracking-tight text-zinc-400">Credit Limit</p>
                                        <p className="text-2xl font-extrabold text-zinc-900">
                                            ₦{Number(creditline.credit_limit || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                    <div className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                                        <p className="text-[11px] font-bold uppercase tracking-tight text-zinc-400">Current Balance</p>
                                        <p className="text-2xl font-extrabold text-emerald-600">
                                            ₦{Number(creditline.current_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                        </p>
                                    </div>
                                </div>
                            ) : formData.customerId && formData.storeId ? (
                                <div className="text-center py-12 text-amber-600">
                                    <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
                                    <p className="font-medium">No active credit line</p>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-zinc-400">
                                    Select customer to view credit status
                                </div>
                            )}
                        </Card>
                    ) : (
                        <Card className="p-6 border-dashed border-zinc-300 bg-zinc-50/70 text-center">
                            <p className="text-zinc-500 text-sm">Group invoices bypass individual credit checks</p>
                        </Card>
                    )}
                </div>

            </div>
        </div>
    );
};

export default NewInvoice;